import {PaperAirplaneIcon, AlertIcon} from '@primer/octicons-react'
import {scheduleAfterNextPaint} from '@github-ui/copilot-chat/utils/schedule-after-next-paint'
import {IconButton} from '@primer/react'
import {useEffect, useState, useRef} from 'react'
import {ScopedCommands} from '@github-ui/ui-commands'
import styles from './SteeringCommandInput.module.css'

// Maximum height for textarea in pixels, equivalent to approximately 6 lines
const MAX_TEXTAREA_HEIGHT_PX = 256

type SteeringCommandInputProps = {
  onSubmit: (message: string) => void
  error?: string | null
}

export function SteeringCommandInput({onSubmit, error}: SteeringCommandInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autosizeFrameRef = useRef<number | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    function adjustHeight() {
      if (!textarea) return
      // Reset first so scrollHeight reflects the content's natural height, including when text shrinks.
      textarea.style.height = 'auto'
      // Then apply the measured height, capped so the textarea scrolls after reaching the max.
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`
    }

    // Resizing the textarea changes its height, reads the new scrollHeight,
    // then writes the height back. If we do all of that before the current
    // frame paints, it delays the paint and hurts INP. That means the typed
    // character can feel sluggish. So we let the browser paint first so the
    // new character shows up quickly. We resize in the next frame so the
    // textarea height still matches the new text without much delay.
    return scheduleAfterNextPaint(autosizeFrameRef, adjustHeight)
  }, [value])

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value)
      setValue('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  return (
    <ScopedCommands commands={{'copilot-chat:send-message': handleSubmit}}>
      <div className={styles.steeringCommandInputContainer}>
        <div className={styles.steeringCommandInputWrapper}>
          <textarea
            ref={textareaRef}
            className={styles.steeringCommandInputTextarea}
            value={value}
            onChange={handleChange}
            placeholder="What would you like to do next?"
            autoComplete="off"
            autoCorrect="on"
            rows={1}
          />
          <IconButton
            className={styles.steeringCommandInputSubmitButton}
            variant="invisible"
            size="small"
            icon={PaperAirplaneIcon}
            aria-label="Send"
            disabled={!value.trim()}
            onClick={handleSubmit}
          />
        </div>
        {error && (
          <div className={styles.errorMessage}>
            <AlertIcon className={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </ScopedCommands>
  )
}
